import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./PhoneRegister.css";
import { countryList } from "../countries";
import { useLanguage } from "../i18n";
import { generateFullFingerprintPayload } from "../Tools/fingerprint";
import { hasSecurityFingerprintConsent } from "../Tools/fingerprintConsent";

const API_URL = "/api";

export default function PhoneRegister({ onClose, onOTP }) {
  const { t, language } = useLanguage();
  const countryNames = new Intl.DisplayNames(
    [language === "zh" ? "zh-Hans" : language],
    { type: "region" }
  );
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fullPhoneNumber = `${countryCode}${phone.replace(/\D/g, "")}`;

  const handleSendCode = async () => {
    setError("");

    if (!phone.trim()) {
      setError(t("pleaseEnterPhone"));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/phone/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          phone: fullPhoneNumber
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (_parseError) {
        throw new Error("phoneApiUnavailable");
      }

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Unable to send verification code.");
      }

      setStep("otp");
    } catch (err) {
      console.error("Phone verification send error:", err);
      setError(t(err.message === "Invalid phone number" ? "invalidPhone" : err.message === "phoneApiUnavailable" ? "phoneApiUnavailable" : "phoneSendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError("");

    if (!/^\d{6}$/.test(code.trim())) {
      setError(t("phoneCodeError"));
      return;
    }

    setLoading(true);

    try {
      const fingerprint = hasSecurityFingerprintConsent()
        ? await generateFullFingerprintPayload()
        : null;
      const response = await fetch(`${API_URL}/auth/phone/check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          phone: fullPhoneNumber,
          code: code.trim(),
          fingerprint,
          deviceRecognitionConsent: hasSecurityFingerprintConsent()
        })
      });

      const data = await response.json();

      if (!response.ok || !data.ok || data.verified !== true) {
        throw new Error(data.error || "Invalid verification code.");
      }

      onOTP(data.user);
    } catch (err) {
      console.error("Phone verification check error:", err);
      setError(t("phoneVerifyError"));
    } finally {
      setLoading(false);
    }
  };

  return createPortal((
    <div className="phone-register-overlay">
      <div className="phone-register-card">
        <button className="phone-register-close" onClick={onClose} aria-label={t("close")} type="button">✕</button>

        {step === "phone" && (
          <>
            <h2 className="phone-register-title">{t("phoneTitle")}</h2>
            <p className="phone-register-subtitle">
              {t("phoneSendIntro")}
            </p>

            <div className="phone-register-fields">
              <select
                className="phone-register-country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={loading}
              >
                {countryList.map((c) => (
                  <option key={c.countryCode} value={c.code}>
                    {c.flag} {c.code} — {countryNames.of(c.countryCode) || c.name}
                  </option>
                ))}
              </select>

              <input
                type="tel"
                className="phone-register-input"
                placeholder={t("phonePlaceholder")}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && <p className="phone-register-error" role="alert">{error}</p>}

            <button
              className="phone-register-button"
              onClick={handleSendCode}
              disabled={loading}
            >
              {loading ? t("sending") : t("continue")}
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <h2 className="phone-register-title">{t("enterCode")}</h2>
            <p className="phone-register-subtitle">
              {t("phoneSentIntro")}
            </p>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-label={t("phoneCodePlaceholder")}
              className="phone-register-input phone-register-code"
              placeholder={t("phoneCodePlaceholder")}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={loading}
            />

            {error && <p className="phone-register-error" role="alert">{error}</p>}

            <button
              className="phone-register-button"
              onClick={handleVerifyCode}
              disabled={loading || code.length !== 6}
            >
              {loading ? t("verifying") : t("verify")}
            </button>
            <button
              type="button"
              className="phone-register-back"
              onClick={() => { setStep("phone"); setCode(""); setError(""); }}
              disabled={loading}
            >
              {t("back")}
            </button>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
